<template>
  <section class="py-16 md:py-24 bg-gradient-to-b from-[#f0f5f4] to-white">
    <div class="container mx-auto px-4 md:px-6">
      <!-- Section Header -->
      <div
        class="text-center max-w-4xl mx-auto mb-16 md:mb-24"
        data-aos="fade-up"
        data-aos-duration="800"
      >
        <span
          class="inline-block px-6 py-2 rounded-full bg-green-100 text-green-800 text-base font-medium mb-6"
        >
          Why Choose Us
        </span>
        <h2
          class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-[#164364]"
        >
          Revolutionizing <span class="text-green-800">Agriculture</span>
        </h2>
        <p class="text-lg md:text-xl lg:text-2xl text-gray-800">
          Our platform creates a digital ecosystem that empowers farmers,
          connects markets, and drives sustainable agricultural growth.
        </p>
      </div>

      <!-- Feature Tabs -->
      <div class="mb-16 md:mb-20">
        <!-- Tab Navigation -->
        <div
          class="flex flex-wrap justify-center gap-4 md:gap-6 mb-8 md:mb-12"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="100"
        >
          <button
            class="px-4 md:px-8 py-2 md:py-4 rounded-lg text-base md:text-lg lg:text-xl font-medium transition-all duration-300 shadow-md"
            :class="[
              activeTab === 'access'
                ? 'bg-green-800 text-white'
                : 'bg-white text-gray-700 hover:bg-green-50',
            ]"
            @click="activeTab = 'access'"
          >
            <i class="fas fa-mobile-alt mr-2 md:mr-3 text-base md:text-xl"></i>
            Accessibility
          </button>
          <button
            class="px-4 md:px-8 py-2 md:py-4 rounded-lg text-base md:text-lg lg:text-xl font-medium transition-all duration-300 shadow-md"
            :class="[
              activeTab === 'connect'
                ? 'bg-green-800 text-white'
                : 'bg-white text-gray-700 hover:bg-green-50',
            ]"
            @click="activeTab = 'connect'"
          >
            <i class="fas fa-users mr-2 md:mr-3 text-base md:text-xl"></i>
            Connectivity
          </button>
          <button
            class="px-4 md:px-8 py-2 md:py-4 rounded-lg text-base md:text-lg lg:text-xl font-medium transition-all duration-300 shadow-md"
            :class="[
              activeTab === 'data'
                ? 'bg-green-800 text-white'
                : 'bg-white text-gray-700 hover:bg-green-50',
            ]"
            @click="activeTab = 'data'"
          >
            <i class="fas fa-chart-bar mr-2 md:mr-3 text-base md:text-xl"></i>
            Data Insights
          </button>
        </div>

        <!-- Tab Content -->
        <div
          v-if="activeTab === 'access'"
          class="bg-white rounded-xl shadow-lg"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          <div class="flex flex-col-reverse lg:flex-row">
            <div class="lg:w-1/2 p-6 md:p-8 lg:p-12">
              <div class="mb-6">
                <span
                  class="inline-block px-4 py-2 text-sm font-medium bg-green-100 text-green-800 rounded-full"
                >
                  Feature 01
                </span>
                <h3
                  class="text-2xl md:text-3xl lg:text-4xl font-bold text-[#164364] mt-3"
                >
                  Access Anywhere, Anytime
                </h3>
              </div>
              <p class="text-base md:text-xl text-gray-800 mb-6 md:mb-8">
                Our platform works seamlessly across all devices with adaptive
                interfaces. From smartphones in the field to desktops at
                cooperatives, your agricultural toolkit is always available.
              </p>
              <ul class="space-y-3 md:space-y-5">
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Responsive design works across all devices</span
                  >
                </li>
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Progressive Web App for offline capabilities</span
                  >
                </li>
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Low-bandwidth optimization for rural areas</span
                  >
                </li>
              </ul>
            </div>
            <div class="lg:w-1/2 h-64 md:h-96 lg:h-[450px] overflow-hidden">
              <img
                :src="require('@/assets/images/access1.jpg')"
                alt="Farmer using app on mobile device"
                class="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <div
          v-else-if="activeTab === 'connect'"
          class="bg-white rounded-xl shadow-lg"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          <div class="flex flex-col-reverse lg:flex-row">
            <div class="lg:w-1/2 p-6 md:p-8 lg:p-12">
              <div class="mb-6">
                <span
                  class="inline-block px-4 py-2 text-sm font-medium bg-green-100 text-green-800 rounded-full"
                >
                  Feature 02
                </span>
                <h3
                  class="text-2xl md:text-3xl lg:text-4xl font-bold text-[#164364] mt-3"
                >
                  Connected Agricultural Ecosystem
                </h3>
              </div>
              <p class="text-base md:text-xl text-gray-800 mb-6 md:mb-8">
                Our platform bridges critical gaps between all agricultural
                stakeholders, creating a seamless network that benefits everyone
                in the value chain.
              </p>
              <ul class="space-y-3 md:space-y-5">
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Direct marketplace connecting farmers and buyers</span
                  >
                </li>
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Integrated logistics and transport coordination</span
                  >
                </li>
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Access to financial services and agricultural inputs</span
                  >
                </li>
              </ul>
            </div>
            <div class="lg:w-1/2 h-64 md:h-96 lg:h-[450px] overflow-hidden">
              <img
                :src="require('@/assets/images/connect.png')"
                alt="Agricultural ecosystem connection"
                class="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <div
          v-else-if="activeTab === 'data'"
          class="bg-white rounded-xl shadow-lg"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          <div class="flex flex-col-reverse lg:flex-row">
            <div class="lg:w-1/2 p-6 md:p-8 lg:p-12">
              <div class="mb-6">
                <span
                  class="inline-block px-4 py-2 text-sm font-medium bg-green-100 text-green-800 rounded-full"
                >
                  Feature 03
                </span>
                <h3
                  class="text-2xl md:text-3xl lg:text-4xl font-bold text-[#164364] mt-3"
                >
                  Actionable Data Insights
                </h3>
              </div>
              <p class="text-base md:text-xl text-gray-800 mb-6 md:mb-8">
                Transform raw agricultural data into actionable insights that
                drive better decision-making. Our analytics engine helps
                optimize planting, harvesting, and selling strategies.
              </p>
              <ul class="space-y-3 md:space-y-5">
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Personalized dashboards with market trends</span
                  >
                </li>
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Weather forecasting and crop planning tools</span
                  >
                </li>
                <li class="flex items-start">
                  <span
                    class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 md:mr-4"
                  >
                    <i
                      class="fas fa-check text-sm md:text-base text-green-800"
                    ></i>
                  </span>
                  <span class="text-base md:text-lg text-gray-800"
                    >Yield prediction and optimization recommendations</span
                  >
                </li>
              </ul>
            </div>
            <div class="lg:w-1/2 h-64 md:h-96 lg:h-[450px] overflow-hidden">
              <img
                :src="require('@/assets/images/data1.png')"
                alt="Agricultural data insights dashboard"
                class="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- CTA -->
    </div>
  </section>
</template>

<script>
export default {
  name: "AgriculturalPlatformFeatures",
  data() {
    return {
      activeTab: "access",
    };
  },
  mounted() {
    // Add Font Awesome if not already loaded
    if (!document.getElementById("font-awesome-css")) {
      const link = document.createElement("link");
      link.id = "font-awesome-css";
      link.rel = "stylesheet";
      link.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css";
      document.head.appendChild(link);
    }
  },
};
</script>

<style scoped>
@keyframes gradient-x {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.animate-gradient-x {
  background-size: 200% 100%;
  animation: gradient-x 3s ease infinite;
}
</style>
